import React from 'react';
import * as XLSX from 'xlsx';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const stepLabels = [
  'Upload File',
  'Verify Well Positions',
  'Inspect Controls',
  'Review Results',
];
const defaultWellPositions = {
  POS: ['A:1', 'A:2'],
  NEG: ['B:1', 'B:2'],
  STD: ['C:1', 'C:2'],
};
const defaultRowsByGroup = {
  POS: 'A',
  NEG: 'B',
  STD: 'C',
};
const CONTROL_RULES = {
  POS: {
    label: 'Positive Control',
    scoreLabel: '>= 1.200',
    passesScore: (score) => score >= 1.2,
    cvMax: 15,
  },
  NEG: {
    label: 'Negative Control',
    scoreLabel: '< 0.930',
    passesScore: (score) => score < 0.93,
    cvMax: 20,
  },
  STD: {
    label: 'Standard',
    scoreLabel: '< 0.880',
    passesScore: (score) => score < 0.88,
    cvMax: 15,
  },
};
const DEFAULT_LOT_CORRECTION_FACTOR = 1.26;

function getDefaultWellFor(group, index) {
  return `${defaultRowsByGroup[group]}:${index + 1}`;
}

function normalizeWellPosition(value) {
  const match = String(value || '').trim().match(/^([A-Za-z])\s*:?\s*(\d+)$/);
  if (!match) {
    return String(value || '').trim().toUpperCase();
  }
  const [, row, rawColumn] = match;
  return `${row.toUpperCase()}:${String(parseInt(rawColumn, 10))}`;
}

function normalizeLookupNumber(value) {
  const trimmed = String(value ?? '').trim();
  if (!/^\d+$/.test(trimmed)) {
    return trimmed;
  }
  return String(parseInt(trimmed, 10));
}

function getCellText(value) {
  return String(value ?? '').trim();
}

function getNumericValue(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const trimmed = String(value ?? '')
    .replace(/,/g, '')
    .trim();

  if (!trimmed || trimmed.startsWith('=')) {
    return null;
  }

  const percent = trimmed.endsWith('%');
  const numericText = percent ? trimmed.slice(0, -1).trim() : trimmed;
  const parsed = Number(numericText);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return percent ? parsed / 100 : parsed;
}

function isPlateRowLabel(value) {
  return /^[A-Z]$/.test(getCellText(value).toUpperCase());
}

function detectPlateBlocks(rows) {
  const blocks = [];

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];
    let runStart = null;
    let bestRun = null;

    for (let cellIndex = 0; cellIndex <= row.length; cellIndex += 1) {
      const normalizedValue =
        cellIndex < row.length ? normalizeLookupNumber(row[cellIndex]) : '';
      const isNumericHeader = /^\d+$/.test(normalizedValue);

      if (isNumericHeader && runStart === null) {
        runStart = cellIndex;
      }

      if ((!isNumericHeader || cellIndex === row.length) && runStart !== null) {
        const runLength = cellIndex - runStart;
        if (runLength >= 2 && (!bestRun || runLength > bestRun.length)) {
          bestRun = { start: runStart, length: runLength };
        }
        runStart = null;
      }
    }

    if (!bestRun || bestRun.start === 0) {
      continue;
    }

    const labelColumnIndex = bestRun.start - 1;
    const columnLabels = row
      .slice(bestRun.start, bestRun.start + bestRun.length)
      .map((cell) => normalizeLookupNumber(cell));

    let scanRowIndex = rowIndex + 1;
    const blockRows = [];

    while (scanRowIndex < rows.length) {
      const scanRow = rows[scanRowIndex] || [];
      const rowLabel = getCellText(scanRow[labelColumnIndex]).toUpperCase();
      if (!isPlateRowLabel(rowLabel)) {
        break;
      }

      const values = columnLabels.map(
        (_, offset) => scanRow[bestRun.start + offset] ?? ''
      );
      blockRows.push({
        rowIndex: scanRowIndex,
        rowLabel,
        values,
      });
      scanRowIndex += 1;
    }

    if (blockRows.length === 0) {
      continue;
    }

    const entries = [];
    const cellMap = {};
    blockRows.forEach(({ rowLabel, values }) => {
      values.forEach((rawValue, valueIndex) => {
        const columnLabel = columnLabels[valueIndex];
        const position = `${rowLabel}:${columnLabel}`;
        const textValue = getCellText(rawValue);
        const numericValue = getNumericValue(rawValue);
        const entry = {
          position,
          rowLabel,
          columnLabel,
          rawValue,
          textValue,
          numericValue,
        };
        entries.push(entry);
        cellMap[position] = entry;
      });
    });

    const populatedEntries = entries.filter((entry) => entry.textValue !== '');
    const numericEntryCount = populatedEntries.filter(
      (entry) => entry.numericValue !== null
    ).length;

    blocks.push({
      headerRowIndex: rowIndex,
      labelColumnIndex,
      columnLabels,
      rows: blockRows,
      entries,
      cellMap,
      kind:
        numericEntryCount >= Math.max(1, populatedEntries.length / 2)
          ? 'data'
          : 'mapping',
    });

    rowIndex = scanRowIndex - 1;
  }

  return blocks;
}

function buildRecordResultBlock(rows) {
  if (!rows.length) {
    return null;
  }

  const headerRow = rows[0].map((cell) => getCellText(cell).toUpperCase());
  const wellPositionIndex = headerRow.indexOf('WELLPOSITION');
  const rluIndex = headerRow.indexOf('RLU');
  const idIndex = headerRow.indexOf('ID');
  const sequenceIdIndex = headerRow.indexOf('SEQUENCEID');
  const tagIndex = headerRow.indexOf('TAG');
  const timestampIndex = headerRow.findIndex((cell) => cell.startsWith('TIMESTAMP'));

  if (wellPositionIndex === -1 || rluIndex === -1) {
    return null;
  }

  const entriesByPosition = new Map();
  rows.slice(1).forEach((row) => {
    const position = normalizeWellPosition(row[wellPositionIndex]);
    if (!/^[A-Z]:\d+$/.test(position)) {
      return;
    }

    const numericValue = getNumericValue(row[rluIndex]);
    const textValue = getCellText(row[rluIndex]);
    const [rowLabel, columnLabel] = position.split(':');
    const sampleId =
      getCellText(row[tagIndex]) ||
      getCellText(row[idIndex]) ||
      getCellText(row[sequenceIdIndex]);
    const timestampRaw = timestampIndex >= 0 ? row[timestampIndex] : '';
    entriesByPosition.set(position, {
      position,
      rowLabel,
      columnLabel,
      rawValue: row[rluIndex],
      textValue,
      numericValue,
      sampleId,
      timestampRaw,
      sourceRow: row,
    });
  });

  if (entriesByPosition.size === 0) {
    return null;
  }

  const entries = Array.from(entriesByPosition.values()).sort((left, right) => {
    if (left.rowLabel !== right.rowLabel) {
      return left.rowLabel.localeCompare(right.rowLabel);
    }
    return Number(left.columnLabel) - Number(right.columnLabel);
  });

  const rowLabels = [...new Set(entries.map((entry) => entry.rowLabel))];
  const columnLabels = [...new Set(entries.map((entry) => entry.columnLabel))].sort(
    (left, right) => Number(left) - Number(right)
  );
  const rowsByLabel = rowLabels.map((rowLabel) => ({
    rowLabel,
    values: columnLabels.map(
      (columnLabel) => entriesByPosition.get(`${rowLabel}:${columnLabel}`)?.rawValue ?? ''
    ),
  }));

  return {
    kind: 'data',
    source: 'records',
    columnLabels,
    rows: rowsByLabel,
    entries,
    cellMap: Object.fromEntries(entries.map((entry) => [entry.position, entry])),
  };
}

function resolveBlockEntry(block, position) {
  const normalizedPosition = normalizeWellPosition(position);
  const entry = block?.cellMap?.[normalizedPosition];
  if (!entry) {
    return {
      found: false,
      normalizedPosition,
      textValue: '',
      numericValue: null,
    };
  }

  return {
    found: entry.textValue !== '' || entry.numericValue !== null,
    normalizedPosition,
    textValue: entry.textValue,
    numericValue: entry.numericValue,
  };
}

function findNumericCellNearLabel(rows, labelText) {
  const normalizedLabelText = labelText.toUpperCase();

  for (const row of rows) {
    for (let cellIndex = 0; cellIndex < row.length; cellIndex += 1) {
      const cellText = getCellText(row[cellIndex]).toUpperCase();
      if (!cellText.includes(normalizedLabelText)) {
        continue;
      }

      for (let lookupIndex = cellIndex + 1; lookupIndex < row.length; lookupIndex += 1) {
        const numericValue = getNumericValue(row[lookupIndex]);
        if (numericValue !== null) {
          return numericValue;
        }
      }
    }
  }

  return null;
}

function calculateAverage(values) {
  if (values.length === 0) {
    return null;
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function calculateCvPercent(values) {
  if (values.length === 0) {
    return null;
  }
  const average = calculateAverage(values);
  if (!average) {
    return null;
  }
  if (values.length === 1) {
    return 0;
  }

  const variance =
    values.reduce((sum, value) => sum + (value - average) ** 2, 0) /
    (values.length - 1);
  return (Math.sqrt(variance) / average) * 100;
}

function formatRlu(value) {
  if (value === null || !Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function formatScore(value) {
  if (value === null || !Number.isFinite(value)) {
    return '—';
  }
  return value.toFixed(3);
}

function formatPercent(value) {
  if (value === null || !Number.isFinite(value)) {
    return '—';
  }
  return `${value.toFixed(1)}%`;
}

function formatLocalTimestamp(value) {
  const numericValue = getNumericValue(value);
  if (numericValue === null) {
    return getCellText(value) || '—';
  }

  const timestampMilliseconds =
    numericValue >= 1_000_000_000_000 ? numericValue : numericValue * 1000;
  const date = new Date(timestampMilliseconds);

  if (Number.isNaN(date.getTime())) {
    return getCellText(value) || '—';
  }

  return date.toLocaleString();
}

async function parseUploadedFile(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheets = workbook.SheetNames.map((name) => ({
    name,
    rows: XLSX.utils.sheet_to_json(workbook.Sheets[name], {
      header: 1,
      raw: false,
      defval: '',
      blankrows: false,
    }),
  }));

  return {
    name: file.name,
    sheets,
  };
}

export default function ImportBatchDialog({ open, onClose }) {
  const [activeStep, setActiveStep] = React.useState(0);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [wellPositions, setWellPositions] = React.useState(defaultWellPositions);
  const [parsedFile, setParsedFile] = React.useState(null);
  const [selectedSheetName, setSelectedSheetName] = React.useState('');
  const [parseError, setParseError] = React.useState('');
  const [isParsingFile, setIsParsingFile] = React.useState(false);
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [lotCorrectionFactorInput, setLotCorrectionFactorInput] = React.useState(
    String(DEFAULT_LOT_CORRECTION_FACTOR)
  );
  const dragDepthRef = React.useRef(0);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setActiveStep(0);
    setSelectedFile(null);
    setWellPositions(defaultWellPositions);
    setParsedFile(null);
    setSelectedSheetName('');
    setParseError('');
    setIsParsingFile(false);
    setIsDragActive(false);
    setLotCorrectionFactorInput(String(DEFAULT_LOT_CORRECTION_FACTOR));
  }, [open]);

  const activeSheet = React.useMemo(
    () => parsedFile?.sheets.find((sheet) => sheet.name === selectedSheetName) || parsedFile?.sheets[0] || null,
    [parsedFile, selectedSheetName]
  );

  const detectedPlateBlocks = React.useMemo(
    () => detectPlateBlocks(activeSheet?.rows || []),
    [activeSheet]
  );

  const recordResultBlock = React.useMemo(
    () => buildRecordResultBlock(activeSheet?.rows || []),
    [activeSheet]
  );

  const mappingBlock = React.useMemo(
    () => detectedPlateBlocks.find((block) => block.kind === 'mapping') || detectedPlateBlocks[0] || null,
    [detectedPlateBlocks]
  );

  const resultBlock = React.useMemo(
    () =>
      recordResultBlock ||
      [...detectedPlateBlocks].reverse().find((block) => block.kind === 'data') ||
      detectedPlateBlocks[0] ||
      null,
    [detectedPlateBlocks, recordResultBlock]
  );

  const detectedLotCorrectionFactor = React.useMemo(() => {
    if (!activeSheet) {
      return DEFAULT_LOT_CORRECTION_FACTOR;
    }
    return (
      findNumericCellNearLabel(activeSheet.rows, 'LOT CORRECTION FACTOR') ??
      DEFAULT_LOT_CORRECTION_FACTOR
    );
  }, [activeSheet]);

  React.useEffect(() => {
    if (!activeSheet) {
      return;
    }
    setLotCorrectionFactorInput(String(detectedLotCorrectionFactor));
  }, [activeSheet, detectedLotCorrectionFactor]);

  const controlMappings = React.useMemo(() => {
    if (!mappingBlock || mappingBlock.kind !== 'mapping') {
      return {};
    }

    return {
      POS: wellPositions.POS.map((position) => resolveBlockEntry(mappingBlock, position)),
      NEG: wellPositions.NEG.map((position) => resolveBlockEntry(mappingBlock, position)),
      STD: wellPositions.STD.map((position) => resolveBlockEntry(mappingBlock, position)),
    };
  }, [mappingBlock, wellPositions]);

  const lotCorrectionFactor = getNumericValue(lotCorrectionFactorInput);

  const resultSummary = React.useMemo(() => {
    if (!resultBlock) {
      return null;
    }

    const perGroup = Object.keys(CONTROL_RULES).reduce((accumulator, groupKey) => {
      const wells = wellPositions[groupKey].map((position) =>
        resolveBlockEntry(resultBlock, position)
      );
      const numericValues = wells
        .map((entry) => entry.numericValue)
        .filter((value) => value !== null);
      accumulator[groupKey] = {
        wells,
        values: numericValues,
        averageRlu: calculateAverage(numericValues),
        cvPercent: calculateCvPercent(numericValues),
      };
      return accumulator;
    }, {});

    const cutoff =
      perGroup.STD.averageRlu !== null && lotCorrectionFactor !== null
        ? perGroup.STD.averageRlu * lotCorrectionFactor
        : null;

    Object.entries(CONTROL_RULES).forEach(([groupKey, rule]) => {
      const group = perGroup[groupKey];
      group.score =
        group.averageRlu !== null && cutoff
          ? group.averageRlu / cutoff
          : null;
      group.passed =
        group.wells.length > 0 &&
        group.wells.every((entry) => entry.numericValue !== null) &&
        group.score !== null &&
        group.cvPercent !== null &&
        rule.passesScore(group.score) &&
        group.cvPercent <= rule.cvMax;
    });

    const controlPositions = new Set(
      Object.values(wellPositions).flat().map((position) => normalizeWellPosition(position))
    );
    const samples = resultBlock.entries
      .filter(
        (entry) =>
          entry.numericValue !== null && !controlPositions.has(entry.position)
      )
      .map((entry) => {
        const mappingEntry = resolveBlockEntry(mappingBlock, entry.position);
        const score = cutoff ? entry.numericValue / cutoff : null;
        return {
          ...entry,
          mappingLabel: mappingEntry.textValue,
          score,
          result:
            score === null ? 'Pending' : score >= 1 ? 'Positive' : 'Negative',
        };
      });

    const positiveCount = samples.filter((sample) => sample.result === 'Positive').length;
    const negativeCount = samples.filter((sample) => sample.result === 'Negative').length;
    const sampleCount = samples.length;

    return {
      cutoff,
      perGroup,
      samples,
      positiveCount,
      negativeCount,
      positivePercent: sampleCount > 0 ? (positiveCount / sampleCount) * 100 : null,
      negativePercent: sampleCount > 0 ? (negativeCount / sampleCount) * 100 : null,
      controlsPassed: Object.values(perGroup).every((group) => group.passed),
    };
  }, [lotCorrectionFactor, mappingBlock, resultBlock, wellPositions]);

  const handleWellPositionChange = (group, index, nextValue) => {
    setWellPositions((prev) => ({
      ...prev,
      [group]: prev[group].map((position, positionIndex) =>
        positionIndex === index ? nextValue.toUpperCase() : position
      ),
    }));
  };

  const handleWellPositionBlur = (group, index) => {
    setWellPositions((prev) => ({
      ...prev,
      [group]: prev[group].map((position, positionIndex) =>
        positionIndex === index ? normalizeWellPosition(position) : position
      ),
    }));
  };

  const handleAddWellPosition = (group) => {
    setWellPositions((prev) => ({
      ...prev,
      [group]: [...prev[group], getDefaultWellFor(group, prev[group].length)],
    }));
  };

  const handleRemoveWellPosition = (group, index) => {
    setWellPositions((prev) => ({
      ...prev,
      [group]: prev[group].filter((_, positionIndex) => positionIndex !== index),
    }));
  };

  const processSelectedFile = async (file) => {
    setSelectedFile(file);
    setParsedFile(null);
    setSelectedSheetName('');
    setParseError('');

    if (!file) {
      return;
    }

    setIsParsingFile(true);
    try {
      const nextParsedFile = await parseUploadedFile(file);
      setParsedFile(nextParsedFile);
      setSelectedSheetName(nextParsedFile.sheets[0]?.name || '');
    } catch (error) {
      setParseError(error?.message || 'Unable to read uploaded file.');
    } finally {
      setIsParsingFile(false);
    }
  };

  const handleFileSelection = async (event) => {
    const file = event.target.files?.[0] || null;
    await processSelectedFile(file);
    event.target.value = '';
  };

  const handleDialogClose = (_, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  const handleDragEnter = (event) => {
    if (activeStep !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragOver = (event) => {
    if (activeStep !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    if (!isDragActive) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (event) => {
    if (activeStep !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const handleDrop = async (event) => {
    if (activeStep !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    const file = event.dataTransfer.files?.[0] || null;
    await processSelectedFile(file);
  };

  const handleNext = () => {
    if (activeStep < stepLabels.length - 1) {
      setActiveStep((prev) => prev + 1);
      return;
    }
    onClose();
  };

  const renderUploadStep = () => (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Upload a CSV or Excel workbook to inspect the batch locally.
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderStyle: 'dashed',
          borderColor: '#b9c8d1',
          borderRadius: 2,
          backgroundColor: '#f8fbfc',
        }}
      >
        <Stack spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
          <Button
            variant="contained"
            component="span"
            sx={{ position: 'relative', overflow: 'hidden' }}
          >
            Upload File
            <Box
              component="input"
              type="file"
              accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileSelection}
              aria-label="Upload batch file"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
              }}
            />
          </Button>
          <Typography variant="body2" color="text.secondary">
            {selectedFile ? selectedFile.name : 'No file selected yet.'}
          </Typography>
          {isParsingFile && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Reading file locally...
              </Typography>
            </Stack>
          )}
          {parseError && <Alert severity="error">{parseError}</Alert>}
          {parsedFile && !parseError && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${parsedFile.sheets.length} sheet${parsedFile.sheets.length === 1 ? '' : 's'}`} />
              <Chip size="small" label={`${activeSheet?.rows.length || 0} rows`} />
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  );

  const renderWellPositionStep = () => (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 2 }}
        sx={{ alignItems: 'flex-start' }}
      >
        {[
          { key: 'POS', label: 'Positive Wells' },
          { key: 'NEG', label: 'Negative Wells' },
          { key: 'STD', label: 'Standard Wells' },
        ].map(({ key, label }) => (
          <Box key={key} sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }}>
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {label}
              </Typography>
              {wellPositions[key].map((position, index) => (
                <Stack
                  key={`${key}-${index}`}
                  direction="row"
                  spacing={0.5}
                  alignItems="flex-start"
                >
                  <TextField
                    value={position}
                    onChange={(event) =>
                      handleWellPositionChange(key, index, event.target.value)
                    }
                    onBlur={() => handleWellPositionBlur(key, index)}
                    size="small"
                    sx={{ width: 110 }}
                  />
                  <IconButton
                    aria-label={`Remove ${key} ${index + 1}`}
                    onClick={() => handleRemoveWellPosition(key, index)}
                    disabled={wellPositions[key].length === 1}
                    size="small"
                    sx={{ mt: 0.25 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                variant="text"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddWellPosition(key)}
                sx={{ px: 0, alignSelf: 'flex-start', minWidth: 0 }}
              >
                Add
              </Button>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Paper>
  );

  const renderResultsStep = () => (
    <Stack spacing={1.5}>
      {!selectedFile && (
        <Alert severity="info">No file uploaded yet. Upload a file on step one to inspect it here.</Alert>
      )}

      {selectedFile && isParsingFile && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Reading file locally...
          </Typography>
        </Stack>
      )}

      {parseError && <Alert severity="error">{parseError}</Alert>}

      {parsedFile && activeSheet && detectedPlateBlocks.length === 0 && !recordResultBlock && (
        <Alert severity="warning">
          No supported result layout was found in this file yet. Step three
          currently supports either a plate grid or a row-based export with
          `WellPosition` and `RLU` columns.
        </Alert>
      )}

      {parsedFile && activeSheet && (
        <>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={selectedFile.name} />
                  <Chip size="small" label={`${parsedFile.sheets.length} sheet${parsedFile.sheets.length === 1 ? '' : 's'}`} />
                  <Chip size="small" label={`${activeSheet.rows.length} rows`} />
                  {resultBlock?.source === 'records' && (
                    <Chip size="small" label="WellPosition / RLU" />
                  )}
                </Stack>
                {parsedFile.sheets.length > 1 && (
                  <TextField
                    select
                    label="Sheet"
                    value={selectedSheetName}
                    onChange={(event) => setSelectedSheetName(event.target.value)}
                    size="small"
                    sx={{ minWidth: 220 }}
                  >
                    {parsedFile.sheets.map((sheet) => (
                      <MenuItem key={sheet.name} value={sheet.name}>
                        {sheet.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <TextField
                  label="Lot Correction Factor"
                  value={lotCorrectionFactorInput}
                  onChange={(event) => setLotCorrectionFactorInput(event.target.value)}
                  size="small"
                  sx={{ width: 180 }}
                />
                <Chip
                  size="small"
                  color={resultSummary?.controlsPassed ? 'success' : 'warning'}
                  label={
                    resultSummary
                      ? resultSummary.controlsPassed
                        ? 'Controls Pass'
                        : 'Controls Need Review'
                      : 'Awaiting Data'
                  }
                />
                <Chip
                  size="small"
                  label={`RLU Cutoff ${formatRlu(resultSummary?.cutoff ?? null)}`}
                />
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Control</TableCell>
                    <TableCell>Wells</TableCell>
                    <TableCell align="right">Avg RLU</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell align="right">CV</TableCell>
                    <TableCell>Rule</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(CONTROL_RULES).map(([groupKey, rule]) => {
                    const groupSummary = resultSummary?.perGroup?.[groupKey];
                    return (
                      <TableRow key={groupKey}>
                        <TableCell>{rule.label}</TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            {(groupSummary?.wells || []).map((entry) => {
                              const mappingLabel =
                                controlMappings[groupKey]?.find(
                                  (value) =>
                                    value.normalizedPosition === entry.normalizedPosition
                                )?.textValue || '';
                              return (
                                <Typography
                                  key={entry.normalizedPosition}
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {entry.normalizedPosition}
                                  {mappingLabel
                                    ? ` (${mappingLabel})`
                                    : ''}
                                  {`: ${entry.numericValue !== null ? formatRlu(entry.numericValue) : 'Missing'}`}
                                </Typography>
                              );
                            })}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          {formatRlu(groupSummary?.averageRlu ?? null)}
                        </TableCell>
                        <TableCell align="right">
                          {formatScore(groupSummary?.score ?? null)}
                        </TableCell>
                        <TableCell align="right">
                          {formatPercent(groupSummary?.cvPercent ?? null)}
                        </TableCell>
                        <TableCell>{`${rule.scoreLabel} and CV <= ${rule.cvMax.toFixed(1)}%`}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={groupSummary?.passed ? 'success' : 'error'}
                            label={groupSummary?.passed ? 'Pass' : 'Fail'}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {resultSummary && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={`Positive ${resultSummary.positiveCount} (${formatPercent(
                      resultSummary.positivePercent
                    )})`}
                  />
                  <Chip
                    size="small"
                    label={`Negative ${resultSummary.negativeCount} (${formatPercent(
                      resultSummary.negativePercent
                    )})`}
                  />
                </Stack>
              )}
            </Stack>
          </Paper>
        </>
      )}
    </Stack>
  );

  const renderReviewResultsStep = () => (
    <Stack spacing={1.5}>
      {!selectedFile && (
        <Alert severity="info">No file uploaded yet. Upload a file on step one to inspect it here.</Alert>
      )}

      {parseError && <Alert severity="error">{parseError}</Alert>}

      {parsedFile && activeSheet && resultSummary && (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack spacing={1}>
            <Stack spacing={0.75}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Results File Summary
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.entries(CONTROL_RULES).map(([groupKey]) => {
                  const groupSummary = resultSummary.perGroup[groupKey];
                  return (
                    <Chip
                      key={groupKey}
                      size="small"
                      color={groupSummary?.passed ? 'success' : 'default'}
                      label={`${groupKey} AVG ${formatRlu(groupSummary?.averageRlu ?? null)}`}
                    />
                  );
                })}
              </Stack>
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sample ID</TableCell>
                  <TableCell>Well Position</TableCell>
                  <TableCell align="right">RLU</TableCell>
                  <TableCell align="right">Score</TableCell>
                  <TableCell>Timestamp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resultSummary.samples.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No sample wells were detected beyond the configured POS, NEG, and STD wells.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  resultSummary.samples.map((sample) => (
                    <TableRow key={sample.position}>
                      <TableCell>{sample.sampleId || '—'}</TableCell>
                      <TableCell>{sample.position}</TableCell>
                      <TableCell align="right">{formatRlu(sample.numericValue)}</TableCell>
                      <TableCell align="right">{formatScore(sample.score)}</TableCell>
                      <TableCell>{formatLocalTimestamp(sample.timestampRaw)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Stack>
        </Paper>
      )}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          minHeight: { md: 680 },
          maxHeight: 'calc(100vh - 32px)',
        },
      }}
    >
      <DialogTitle>Import Batch</DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 2 },
          overflow: 'hidden',
          position: 'relative',
          backgroundColor:
            activeStep === 0 && isDragActive ? '#f2fbff' : undefined,
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {activeStep === 0 && isDragActive && (
          <Box
            sx={{
              position: 'absolute',
              inset: 12,
              border: '2px dashed #2f6b7a',
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.78)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Drop CSV or Excel file to upload
            </Typography>
          </Box>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Card
            sx={{
              border: '1px solid #d8e2e7',
              boxShadow: 'none',
              borderRadius: 2,
              width: { xs: '100%', md: 216 },
              flexShrink: 0,
            }}
          >
            <CardContent sx={{ p: { xs: 1.5, md: 1.75 } }}>
              <Stepper activeStep={activeStep} orientation="vertical">
                {stepLabels.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>

          <Box
            sx={{
              flex: 1,
              width: '100%',
              minHeight: { md: 560 },
              maxHeight: { md: 560 },
              overflowY: 'auto',
              pr: { md: 0.5 },
            }}
          >
            {activeStep === 0
              ? renderUploadStep()
              : activeStep === 1
                ? renderWellPositionStep()
                : activeStep === 2
                  ? renderResultsStep()
                  : renderReviewResultsStep()}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {activeStep > 0 && <Button onClick={() => setActiveStep(activeStep - 1)}>Back</Button>}
        <Button variant="contained" onClick={handleNext}>
          {activeStep === stepLabels.length - 1 ? 'Import Batch' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
